import { getAccessToken } from "../../utils/auth";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Modal,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "../../utils/api";

// Keyframes for loader
if (typeof document !== "undefined") {
  const styleId = "robotversion-list2-loader-keyframes";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes loading-bar {
        0% { transform: translateX(-60%); }
        50% { transform: translateX(10%); }
        100% { transform: translateX(120%); }
      }
    `;
    document.head.appendChild(style);
  }
}

const styles = {
  pageWrap: {
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    py: { xs: 2, md: 4 },
    px: { xs: 2, sm: 3, md: 4 },
    width: "100%",
    boxSizing: "border-box",
  },
  innerWrap: {
    width: "100%",
    maxWidth: 800,
    margin: "0 auto",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 2,
    mb: 3,
  },
  backButton: {
    bgcolor: "white",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": {
      bgcolor: "#f5f5f5",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
  },
  title: {
    fontWeight: 700,
    color: "#1a1a2e",
    fontSize: { xs: "1.25rem", md: "1.5rem" },
  },
  subtitle: {
    color: "#64748b",
    fontSize: "0.875rem",
    mt: 0.5,
  },
  card: {
    borderRadius: 3,
    overflow: "hidden",
    bgcolor: "white",
    boxShadow: "none",
    border: "1px solid rgba(0,0,0,0.06)",
    p: 3,
  },
  cardContent: {
    py: 2.5,
    px: 0,
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: "1.2rem",
    color: "#1e293b",
    mb: 2,
  },
  metaGrid: {
    flex: 1,
    minHeight: 0,
  },
  actions: {
    display: "flex",
    mt: 2,
    pt: 2,
    borderTop: "1px solid rgba(0,0,0,0.06)",
  },
  updateButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 2,
    px: 3,
    py: 1.25,
    background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)",
    boxShadow: "0 2px 8px rgba(13, 71, 161, 0.3)",
    "&:hover": {
      background: "linear-gradient(135deg, #0a3d91 0%, #0d47a1 100%)",
      boxShadow: "0 4px 12px rgba(13, 71, 161, 0.4)",
    },
  },
};

function MetaRow({ label, value }) {
  return (
    <Box sx={metaRowStyles.row}>
      <Typography sx={metaRowStyles.label}>{label}</Typography>
      <Typography sx={metaRowStyles.value} title={value}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

const metaRowStyles = {
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 1.5,
    py: 0.75,
    borderBottom: "1px solid rgba(0,0,0,0.04)",
    "&:last-of-type": { borderBottom: "none" },
  },
  label: {
    fontSize: "0.8125rem",
    color: "#64748b",
    fontWeight: 500,
    flexShrink: 0,
  },
  value: {
    fontSize: "0.875rem",
    color: "#1e293b",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
    maxWidth: "65%",
    textAlign: "right",
  },
};

const RobotVersionList2 = () => {
  const { emailId } = useParams();
  const [appDetails, setAppDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [manualMapping, setManualMapping] = useState("");
  const [objectDisinfection, setObjectDisinfection] = useState("");
  const navigate = useNavigate();

  const decodeEmail = (encodedEmail) => {
    return atob(decodeURIComponent(encodedEmail));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchAppDetails = async () => {
      try {
        const decodedEmail = decodeEmail(emailId);
        const response = await axios.get(`/appdetails/${decodedEmail}`, {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
          },
        });
        setAppDetails(response.data);
        setManualMapping(response.data.user.manualMapping);
        setObjectDisinfection(response.data.user.objectDisinfection);
      } catch (error) {
        console.error("Error fetching robot details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAppDetails();
  }, [emailId]);

  const handleUpdate = async () => {
    try {
      const decodedEmail = decodeEmail(emailId);
      await axios.put(
        `/update-appdetails/${decodedEmail}`,
        {
          manualMapping,
          objectDisinfection,
        },
        {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
          },
        }
      );

      const response = await axios.get(`/appdetails/${decodedEmail}`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });
      setAppDetails(response.data);
      handleCloseDialog();
    } catch (error) {
      console.error("Error updating user settings:", error);
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
          px: 2,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 260,
            height: 260,
            borderRadius: "28px",
            bgcolor: "rgba(15,23,42,0.9)",
            boxShadow:
              "0 24px 60px rgba(15,23,42,0.55), 0 0 0 1px rgba(148,163,184,0.25)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: "-40%",
              background:
                "conic-gradient(from 180deg at 50% 50%, rgba(56,189,248,0.2), rgba(129,140,248,0.35), rgba(56,189,248,0.2))",
              opacity: 0.8,
              filter: "blur(30px)",
              animation: "spin 12s linear infinite",
            }}
          />
          <Box
            sx={{
              position: "relative",
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: "rgba(15,23,42,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 0 0 1px rgba(148,163,184,0.4), 0 0 0 12px rgba(15,23,42,0.9)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: -2,
                borderRadius: "50%",
                border: "3px solid transparent",
                borderTopColor: "#38bdf8",
                borderRightColor: "#6366f1",
                animation: "spin 1s linear infinite",
              }}
            />
            <Typography
              sx={{
                position: "relative",
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#e5e7eb",
              }}
            >
              S
            </Typography>
          </Box>
          <Box sx={{ position: "relative", textAlign: "center", px: 3 }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 600,
                color: "#e5e7eb",
                mb: 0.5,
              }}
            >
              Loading robot metadata
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>
              Fetching version and settings…
            </Typography>
          </Box>
          <Box
            sx={{
              position: "relative",
              width: "68%",
              height: 4,
              borderRadius: 999,
              bgcolor: "rgba(15,23,42,1)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, transparent, #38bdf8, #6366f1, transparent)",
                animation: "loading-bar 1.4s ease-in-out infinite",
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  if (!appDetails) {
    return (
      <Box sx={styles.pageWrap}>
        <Box sx={styles.innerWrap}>
          <Box sx={styles.header}>
            <IconButton
              onClick={() => navigate(-1)}
              size="medium"
              sx={styles.backButton}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={styles.title}>Robot metadata</Typography>
              <Typography sx={styles.subtitle}>
                Version and app settings for this user
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              px: 2,
              bgcolor: "white",
              borderRadius: 3,
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <Typography sx={{ color: "#64748b", fontWeight: 600 }}>
              No robot details found
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.pageWrap}>
      <Box sx={styles.innerWrap}>
        <Box sx={styles.header}>
          <IconButton
            onClick={() => navigate(-1)}
            size="medium"
            sx={styles.backButton}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={styles.title}>Robot metadata details</Typography>
            <Typography sx={styles.subtitle}>
              Version and app settings for {decodeEmail(emailId)}
            </Typography>
          </Box>
        </Box>

        <Paper elevation={0} sx={styles.card}>
          <Box sx={styles.cardContent}>
            <Typography sx={styles.cardTitle}>
              {appDetails.user?.email || "—"}
            </Typography>
            <Box sx={styles.metaGrid}>
              <MetaRow label="Email ID" value={appDetails.user?.email} />
              <MetaRow label="Version" value={appDetails.version} />
              <MetaRow label="Manual mapping" value={appDetails.user?.manualMapping} />
              <MetaRow label="Object disinfection" value={appDetails.user?.objectDisinfection} />
            </Box>
            <Box sx={styles.actions}>
              <Button
                onClick={handleOpenDialog}
                variant="contained"
                sx={styles.updateButton}
              >
                Update Settings
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Update robot settings card - unchanged per user request */}
      <Modal
        open={dialogOpen}
        onClose={handleCloseDialog}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          sx={{
            p: 4,
            borderRadius: 3,
            width: 400,
            maxWidth: "90%",
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Update Robot Settings
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Manual Mapping</InputLabel>
            <Select
              value={manualMapping}
              onChange={(e) => setManualMapping(e.target.value)}
              label="Manual Mapping"
            >
              <MenuItem value="enabled">Enabled</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 4 }}>
            <InputLabel>Object Disinfection</InputLabel>
            <Select
              value={objectDisinfection}
              onChange={(e) => setObjectDisinfection(e.target.value)}
              label="Object Disinfection"
            >
              <MenuItem value="enabled">Enabled</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{
                color: "#64748b",
                borderColor: "rgba(0,0,0,0.23)",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#475569",
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)",
                boxShadow: "0 2px 8px rgba(13, 71, 161, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #0a3d91 0%, #0d47a1 100%)",
                  boxShadow: "0 4px 12px rgba(13, 71, 161, 0.4)",
                },
              }}
            >
              Save Changes
            </Button>
          </Box>
        </Paper>
      </Modal>
    </Box>
  );
};

export default RobotVersionList2;
